/**
 * Platform adapter registry.
 *
 * `@tenant/core` is bundler- and renderer-agnostic: it never touches `window`,
 * `document`, `localStorage` or any React Native module directly. Anything that
 * differs between web and native is reached through the adapter registered here.
 *
 * Each app registers its implementation once, before the store is created:
 *
 *   import {configurePlatform} from '@tenant/core/platform';
 *   configurePlatform(webPlatform);
 *
 * See `platform/contract.js` for the shape an adapter must satisfy.
 */

// eslint-disable-next-line fp/no-let
let adapter = null;

/**
 * Registers the platform adapter. Call once during app bootstrap.
 *
 * @param {object} impl - An object matching the platform contract.
 * @returns {void}
 */
export const configurePlatform = impl => {
  // eslint-disable-next-line fp/no-mutation
  adapter = impl;
};

/**
 * Retrieves the registered platform adapter.
 *
 * Throws rather than falling back to a stub: a silent no-op adapter would turn
 * a missing bootstrap into confusing "not logged in" behaviour at runtime.
 *
 * @returns {object}
 */
export const platform = () => {
  if (!adapter) {
    throw new Error(
      '[@tenant/core] No platform adapter registered. Call configurePlatform() during app bootstrap.'
    );
  }
  return adapter;
};

/**
 * Whether an adapter has been registered. Useful in tests and in code paths
 * that can degrade gracefully.
 *
 * @returns {boolean}
 */
export const hasPlatform = () => adapter !== null;

/**
 * Reads an environment variable through the adapter.
 *
 * Web reads `REACT_APP_*`, native reads `EXPO_PUBLIC_*` — the names differ, so
 * the lookup itself has to be platform-owned.
 *
 * @param {string} name - Variable name, without platform prefix.
 * @param {*} fallback - Returned when the variable is undefined.
 * @returns {*}
 */
export const env = (name, fallback) => platform().env(name, fallback);
